import React, { useRef } from "react";
import { EyeIcon, EyeOffIcon, FileSearchIcon, InfoIcon, Link2Icon, MailIcon, MessageSquareIcon, UploadIcon, BoxIcon } from "lucide-react";
import { InlinePopover } from "./InlinePopover";
import { documentCount } from "../../utils/conditions";
import { Condition } from "../../types/conditions";
interface RowActionsProps {
  condition: Condition;
  onToggleEmail: () => void;
  onToggleBorrowerVisible: () => void;
  onUpload: () => void;
  onLinkDocuments: () => void;
  onComments: () => void;
}
interface ActionDef {
  label: string;
  icon: BoxIcon;
  onClick?: () => void;
  badge?: number;
  disabled?: boolean;
  /** When defined, renders as an on/off toggle. */
  pressed?: boolean;
  menu?: boolean;
}
const baseBtn = 'relative inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:border-transparent disabled:hover:bg-transparent';
const plainTone = 'border-transparent text-slate-500 hover:border-slate-200 hover:bg-white hover:text-brand';
export function RowActions({
  condition,
  onToggleEmail,
  onToggleBorrowerVisible,
  onUpload,
  onLinkDocuments,
  onComments
}: RowActionsProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const docs = documentCount(condition);
  const actions: ActionDef[] = [{
    label: condition.includeInEmail ? 'Included in email' : 'Include in email',
    icon: MailIcon,
    onClick: onToggleEmail,
    pressed: condition.includeInEmail
  }, {
    label: condition.borrowerVisible ? 'Borrower can see' : 'Hidden from borrower',
    icon: condition.borrowerVisible ? EyeIcon : EyeOffIcon,
    onClick: onToggleBorrowerVisible,
    pressed: condition.borrowerVisible
  }, {
    label: 'Add document',
    icon: UploadIcon,
    menu: true
  }, {
    label: 'Review documents',
    icon: FileSearchIcon,
    badge: docs,
    disabled: docs === 0
  }, {
    label: 'Comments',
    icon: MessageSquareIcon,
    onClick: onComments,
    badge: condition.comments.length
  }, {
    label: 'Condition details',
    icon: InfoIcon
  }];
  return <div className="flex items-center justify-end gap-1" data-id="element-278" data-mp="34it51z97tkhh">
      <input ref={fileRef} type="file" className="sr-only" tabIndex={-1} aria-hidden="true" onChange={e => {
      if (e.target.files?.length) onUpload();
      e.target.value = '';
    }} data-id="element-279" data-mp="34lfbtwoa7vyn" />
      {actions.map(({
      label,
      icon: Icon,
      onClick,
      badge,
      disabled,
      pressed,
      menu
    }) => {
      const isToggle = pressed !== undefined;
      const toneClass = isToggle ? pressed ? 'border-brand bg-brand text-white hover:bg-brand-dark' : 'border-slate-300 bg-white text-slate-400 hover:border-brand hover:text-brand' : plainTone;
      return <div key={label} className="group/action relative" data-id="element-280" data-mp="jtvjgqemzkbw">
            {menu ? <InlinePopover ariaLabel={`Add document to ${condition.name}`} align="right" triggerClassName={`${baseBtn} ${plainTone}`} trigger={<Icon className="h-4 w-4" aria-hidden="true" data-id="element-282" data-mp="mf31zcjh6fex" />} data-id="element-281" data-mp="f23toi3b1imt">
                {close => <div role="menu" className="w-64 rounded-md border border-slate-200 bg-white py-1 shadow-lg" data-id="element-283" data-mp="rbtj8370nh1x">
                    <MenuItem icon={UploadIcon} title="Upload new file" hint="From your computer" onClick={() => {
              close();
              fileRef.current?.click();
            }} data-id="element-284" data-mp="wawmpj7clf7s" />
                    <MenuItem icon={Link2Icon} title="Link existing document" hint="Already in the loan file or portal" onClick={() => {
              close();
              onLinkDocuments();
            }} data-id="element-285" data-mp="15bcqyuvx0qdp" />
                  </div>}
              </InlinePopover> : <button type="button" onClick={onClick} disabled={disabled} aria-pressed={isToggle ? pressed : undefined} aria-label={badge ? `${label} (${badge})` : label} className={`${baseBtn} ${toneClass}`} data-id="element-286" data-mp="1f4jsg4njbm8i">
                <Icon className="h-4 w-4" aria-hidden="true" data-id="element-287" data-mp="1t1ymxnsnak7c" />
                {badge ? <span className="absolute -right-1 -top-1 min-w-[16px] rounded-full bg-brand px-1 text-[10px] font-bold leading-4 text-white ring-2 ring-white" data-id="element-288" data-mp="1szvxb4vemt35">
                    {badge}
                  </span> : null}
              </button>}
            <span role="tooltip" className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 transition-opacity group-hover/action:opacity-100" data-id="element-289" data-mp="1p5kpsdbf3vzt">
              {label}
            </span>
          </div>;
    })}
    </div>;
}
interface MenuItemProps {
  icon: BoxIcon;
  title: string;
  hint: string;
  onClick: () => void;
}
function MenuItem({
  icon: Icon,
  title,
  hint,
  onClick
}: MenuItemProps) {
  return <button type="button" role="menuitem" onClick={onClick} className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus:bg-slate-50 focus:outline-none" data-id="element-290" data-mp="11ep5e19ak32l">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-291" data-mp="2cvf5d2n4fynr" />
      <span data-id="element-292" data-mp="3usiifw7k28b8">
        <span className="block text-sm font-medium text-slate-800" data-id="element-293" data-mp="3urz7izgbok6p">{title}</span>
        <span className="block text-xs text-slate-500" data-id="element-294" data-mp="3lpievoi0f8a">{hint}</span>
      </span>
    </button>;
}